import React, { useEffect, useState, useContext, useMemo } from 'react'
import { UserContext } from '../context'
import { Navigate } from 'react-router-dom'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import axios from 'axios'
import { getAccessToken } from '../services/AuthService'
import { parse, differenceInMinutes } from 'date-fns'
import './CreateScheduleAdmin.css'

const DAYS = ['Lunes','Martes','Miercoles','Jueves','Viernes','Sabado','Domingo']
const START_MIN = 0
const END_MIN   = 24 * 60
const STEP      = 60

const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}
const pad2 = (n) => String(n).padStart(2, '0')
const toHHMM = (mins) => `${pad2(Math.floor(mins/60))}:${pad2(mins%60)}`
const labelHour24 = (mins) => `${pad2(Math.floor(mins/60))}:00`
const labelHour12 = (mins) => {
  const h = Math.floor(mins / 60)
  const suf = h >= 12 ? 'pm' : 'am'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:00 ${suf}`
}
const to12hFromString = (hhmm) => {
  const [hRaw, mRaw] = hhmm.split(':')
  const h = Number(hRaw)
  const m = Number(mRaw)
  const suf = h >= 12 ? 'pm' : 'am'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${pad2(m)} ${suf}`
}
const SLOTS = Array.from({ length: (END_MIN-START_MIN)/STEP }, (_, i) => START_MIN + i*STEP)

const eventSchema = Yup.object().shape({
  title: Yup.string().required('El título es obligatorio'),
  day: Yup.string().required('El día es obligatorio'),
  startTime: Yup.string().required('La hora de inicio es obligatoria'),
  endTime: Yup.string()
    .required('La hora de fin es obligatoria')
    .test('is-greater', 'La clase debe durar al menos una hora', function (value) {
      const { startTime } = this.parent
      const format = 'HH:mm'
      const start = parse(startTime, format, new Date())
      const end = parse(value, format, new Date())
      return differenceInMinutes(end, start) >= 60
    }),
  description: Yup.string().required('La descripción es obligatoria'),
})

function EventForm({ initial, onSave, onDelete, onCancel }) {
  const [form, setForm] = useState(initial)
  const [errors, setErrors] = useState({})

  useEffect(() => setForm(initial), [initial])
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const submit = async (e) => {
    e.preventDefault()
    try {
      await eventSchema.validate(form, { abortEarly: false })
      setErrors({})
      onSave(form)
    } catch (err) {
      if (err.inner) {
        const map = err.inner.reduce((acc, e) => { acc[e.path] = e.message; return acc }, {})
        setErrors(map)
      }
    }
  }

  return (
    <div className="sc__overlay" role="dialog" aria-modal="true">
      <div className="sc__modal">
        <div className="sc__modal-head">
          <h3>{initial.id ? 'Editar clase' : 'Agregar clase'}</h3>
          <button className="btn btn--ghost" onClick={onCancel}>Cerrar</button>
        </div>

        <form className="sc__form" onSubmit={submit}>
          <label>
            Título
            <input value={form.title} onChange={update('title')} />
            {errors.title && <span className="sc__err">{errors.title}</span>}
          </label>

          <div className="sc__row">
            <label>
              Día
              <select value={form.day} onChange={update('day')}>
                {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              {errors.day && <span className="sc__err">{errors.day}</span>}
            </label>

            <label>
              Inicio
              <input type="time" value={form.startTime} onChange={update('startTime')} />
              {errors.startTime && <span className="sc__err">{errors.startTime}</span>}
            </label>

            <label>
              Fin
              <input type="time" value={form.endTime} onChange={update('endTime')} />
              {errors.endTime && <span className="sc__err">{errors.endTime}</span>}
            </label>
          </div>

          <label>
            Descripción
            <input value={form.description} onChange={update('description')} />
            {errors.description && <span className="sc__err">{errors.description}</span>}
          </label>

          <label>
            Color (opcional)
            <input type="color" value={form.color || '#2563eb'} onChange={update('color')} />
          </label>

          <div className="sc__modal-actions">
            {initial.id && (
              <button type="button" className="btn btn--danger" onClick={() => onDelete(initial)}>
                Eliminar
              </button>
            )}
            <button type="submit" className="btn btn--primary">Guardar</button>
          </div>
        </form>
      </div>
    </div>
  )
}

const CreateScheduleAdmin = ({ isLoggedIn }) => {
  const [state] = useContext(UserContext)
  const [events, setEvents] = useState({})
  const [modal, setModal] = useState(null)
  const [clock12h, setClock12h] = useState(true)

  // Compute permission once per render
  const canManage = !!isLoggedIn && !!state.user && state.user.is_staff === true

  // fetch events on mount/when allowed
  useEffect(() => {
    if (!canManage) return
    let cancelled = false

    const fetchEvents = async () => {
      try {
        const url = `${process.env.REACT_APP_BASE_URL}/api/events/`
        const token = getAccessToken()
        const headers = { Authorization: `Bearer ${token}` }
        const res = await axios.get(url, { headers, timeout: 5000 })
        if (cancelled) return
        const map = {}
        res.data.forEach(ev => { map[`${ev.day}-${ev.startTime}`] = ev })
        setEvents(map)
      } catch (err) {
        console.error('Error fetching events', err)
      }
    }
    fetchEvents()
    return () => { cancelled = true }
  }, [canManage])

  // persist draft to localStorage
  useEffect(() => {
    if (!canManage) return
    localStorage.setItem('events', JSON.stringify(events))
  }, [events, canManage])

  const keyFor = (ev) => `${ev.day}-${ev.startTime}`

  const hasConflict = (candidate, ignoreKey = null) => {
    const start = toMinutes(candidate.startTime)
    const end   = toMinutes(candidate.endTime)
    return Object.entries(events).some(([k, ev]) => {
      if (k === ignoreKey) return false
      if (ev.day !== candidate.day) return false
      const s = toMinutes(ev.startTime)
      const e = toMinutes(ev.endTime)
      return (start >= s && start < e) || (end > s && end <= e) || (start <= s && end >= e)
    })
  }

  const openCreateAt = (day, slotMins) => {
    const start = toHHMM(slotMins)
    const end   = toHHMM(Math.min(slotMins + 60, END_MIN))
    setModal({ id: null, title: '', day, startTime: start, endTime: end, description: '', color: '#2563eb' })
  }

  const openEdit = (ev) => setModal({ ...ev })

  const saveFromModal = (form) => {
    const candidate = { ...form }
    const k = keyFor(candidate)
    const ignoreKey = modal?.id ? keyFor(modal) : null

    if (hasConflict(candidate, ignoreKey)) {
      toast.error('Esta clase se cruza con otra en el mismo día/hora.')
      return
    }

    setEvents(prev => {
      const next = { ...prev }
      if (ignoreKey && ignoreKey !== k) delete next[ignoreKey]
      next[k] = candidate
      return next
    })
    setModal(null)
    toast.success('Clase agregada/actualizada (recuerda “Guardar horario”).')
  }

  const deleteFromModal = (ev) => {
    const k = keyFor(ev)
    setEvents(prev => {
      const next = { ...prev }
      delete next[k]
      return next
    })
    setModal(null)
  }

  const saveSchedule = async () => {
    try {
      const url = `${process.env.REACT_APP_BASE_URL}/api/events/`
      const token = getAccessToken()
      const headers = { Authorization: `Bearer ${token}` }
      await axios.post(url, Object.values(events), { headers, timeout: 5000 })
      toast.success('Horario guardado correctamente')
    } catch (err) {
      console.error('Error saving schedule:', err)
      toast.error('No se pudo guardar el horario')
    }
  }

  const rowOf = (hhmm) => Math.floor((toMinutes(hhmm) - START_MIN) / STEP) + 1
  const eventsByDay = useMemo(() => {
    const map = {}
    DAYS.forEach(d => { map[d] = [] })
    Object.values(events).forEach(ev => map[ev.day]?.push(ev))
    return map
  }, [events])

  // Decide what to render AFTER hooks
  if (!canManage) return <Navigate to="/log-in" />

  return (
    <div className="sc">
      <div className="sc__header">
        <h1>Horarios Sat Nam Escuela</h1>
        <div className="sc__actions">
          <button className="btn btn--primary" onClick={saveSchedule}>Guardar horario</button>
          <button className="btn btn--ghost" onClick={() => setClock12h(v => !v)} title="Cambiar formato de hora">
            {clock12h ? 'Mostrar 24h' : 'Mostrar 12h'}
          </button>
        </div>
      </div>

      <div className="sc__sheet">
        <div className="sc__toprow">
          <div className="sc__corner">Horas</div>
          {DAYS.map(d => (<div key={d} className="sc__dayhead">{d}</div>))}
        </div>

        <div className="sc__grid">
          <div className="sc__timeline">
            {SLOTS.map((m) => (
              <div key={m} className="sc__time">
                {clock12h ? labelHour12(m) : labelHour24(m)}
              </div>
            ))}
          </div>

          <div className="sc__cols">
            {DAYS.map(day => (
              <div key={day} className="sc__col" style={{ '--rows': SLOTS.length }}>
                {SLOTS.map((m, idx) => (
                  <div
                    key={`${day}-${m}`}
                    className="sc__cell"
                    onClick={() => openCreateAt(day, m)}
                    title={`Añadir clase ${day} ${clock12h ? labelHour12(m) : labelHour24(m)}`}
                    style={{ gridRow: `${idx+1} / span 1` }}
                  />
                ))}

                {eventsByDay[day].map(ev => (
                  <button
                    key={keyFor(ev)}
                    className="sc__event"
                    style={{
                      gridRow: `${rowOf(ev.startTime)} / ${rowOf(ev.endTime)}`,
                      '--ev': ev.color || '#2563eb'
                    }}
                    onClick={(e) => { e.stopPropagation(); openEdit(ev) }}
                    title={`${ev.title} (${ev.startTime}–${ev.endTime})`}
                  >
                    <span className="sc__ev-title">{ev.title}</span>
                    <span className="sc__ev-time">
                      {clock12h ? to12hFromString(ev.startTime) : ev.startTime}
                      –
                      {clock12h ? to12hFromString(ev.endTime) : ev.endTime}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {modal && (
        <EventForm
          initial={modal}
          onSave={saveFromModal}
          onDelete={deleteFromModal}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  )
}

export default CreateScheduleAdmin
